import React, { useState, useRef, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  styled,
  alpha,
  Chip, 
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  CircularProgress,
  Snackbar,
  Alert,
  Switch,
  Card,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  ContentPaste as ContentPasteIcon,
  InsertDriveFile as FileIcon,
  Email as EmailIcon,      
  Cancel as CancelIcon    
} from '@mui/icons-material';

// --- IMPORT FOOTER ---
import Newfooter from '../../NewFooter/Newfooter'; 

// --- GLOBAL CONSTANTS ---
import { VIPSDataServiceURL, requestOptions } from '../../../Constants/GlobalConstant';

// --- CONSTANTS MATCHED TO AJK CUSTOMER ---
const PRIMARY_COLOR = '#1565c0'; 
const BACKGROUND_BG = '#f0f4f8'; 
const HOVER_ROW_COLOR = '#eef6fc';
const HEADER_BG = '#1e293b'; 
const BUTTON_GRADIENT = 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)';

// --- STYLES ---
const PremiumChipContainer = styled(Box)(({ theme, active }) => ({
  display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', minHeight: '46px',
  padding: '6px 14px', borderRadius: '10px', backgroundColor: active ? '#ffffff' : '#f1f5f9',
  border: '1.5px solid', borderColor: active ? '#e2e8f0' : '#cbd5e1',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)', flexGrow: 1, position: 'relative',
  '&:hover': { borderColor: active ? PRIMARY_COLOR : '#cbd5e1' },
  '&:focus-within': {
    borderColor: PRIMARY_COLOR, backgroundColor: '#ffffff',
    boxShadow: `0 0 0 3px ${alpha(PRIMARY_COLOR, 0.12)}`,
  },
  ...( !active && { opacity: 0.7, cursor: 'not-allowed', '& *': { pointerEvents: 'none' } })
}));

const dialogHeaderStyle = {
  backgroundColor: HEADER_BG, color: '#ffffff !important', display: 'flex',
  justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px',
  borderBottom: `4px solid ${PRIMARY_COLOR}`
};

const premiumInputStyle = {
  '& .MuiOutlinedInput-root': {
      backgroundColor: '#fff', borderRadius: 3, transition: 'all 0.2s ease-in-out',
      '& fieldset': { borderColor: '#e0e0e0', borderWidth: '1.5px' },
      '&:hover fieldset': { borderColor: PRIMARY_COLOR },
      '&.Mui-focused fieldset': { borderColor: PRIMARY_COLOR, borderWidth: '2.5px' },
      '&.Mui-disabled fieldset': { borderColor: '#f0f0f0' },
      '&.Mui-disabled': { backgroundColor: '#f9f9f9', color: '#888' }
  },
  '& .MuiInputLabel-root.Mui-focused': { color: PRIMARY_COLOR, fontWeight: 600 },
};

const headerStyle = {
  backgroundColor: HEADER_BG, color: 'white', fontWeight: '600', fontSize: '0.85rem',
  textTransform: 'none', letterSpacing: '0.5px', padding: '12px 16px', whiteSpace: 'nowrap',
  position: 'sticky', top: 0, zIndex: 100 
};

// --- SLIM SWITCH ---
const SlimSwitch = styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 36, height: 20, padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0, margin: 2, transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)', color: '#fff',
      '& + .MuiSwitch-track': { backgroundColor: PRIMARY_COLOR, opacity: 1, border: 0 },
      '&.Mui-disabled + .MuiSwitch-track': { opacity: 0.5 },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': { color: '#33cf4d', border: '6px solid #fff' },
  },
  '& .MuiSwitch-thumb': { boxSizing: 'border-box', width: 16, height: 16 },
  '& .MuiSwitch-track': {
    borderRadius: 20 / 2, backgroundColor: '#E9E9EA', opacity: 1,
    transition: theme.transitions.create(['background-color'], { duration: 500 }),
  },
}));

const CustomSelect = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': { borderRadius: '24px', backgroundColor: '#fff', '& fieldset': { borderColor: '#E0E3E7', borderWidth: '1px' }, '&:hover fieldset': { borderColor: '#B2BAC2' }, '&.Mui-focused fieldset': { borderColor: '#1976d2' } },
  '& .MuiInputLabel-root': { color: '#707EAE', fontSize: '14px', fontWeight: 400, backgroundColor: '#fff', padding: '0 6px', '&.Mui-focused': { color: '#1976d2' } },
  '& .MuiSelect-select': { fontWeight: 500, color: '#1b2559', padding: '12px 20px' },
}));

const UploadBox = styled(Box)(({ theme }) => ({
  border: '2px dashed #E0E3E7', borderRadius: '12px', padding: '30px', textAlign: 'center', cursor: 'pointer', backgroundColor: '#fff', transition: '0.2s', '&:hover': { borderColor: '#1976d2', backgroundColor: alpha('#1976d2', 0.04) }
}));

const decodeHtmlEntities = (str) => {
    if (!str) return '';
    const txt = document.createElement("textarea");
    txt.innerHTML = str;
    return txt.value;
};

const EmailAdmin = () => {
  const userPost = useSelector((state) => state.user);
  const loggedInUserId = userPost?.paxWebSessionStrings?.find(
    (item) => item.Key === 'user_id'
  )?.Value || '';

  const [storeGroup, setStoreGroup] = useState('');
  const [vendorList, setVendorList] = useState('');
  
  const [storeOptions, setStoreOptions] = useState([]);
  const [vendorOptions, setVendorOptions] = useState([]);
  
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false); 
  const [currentInput, setCurrentInput] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [emailComplexityRegex, setEmailComplexityRegex] = useState(null);

  const [openUpload, setOpenUpload] = useState(false);
  const [uploadTab, setUploadTab] = useState(0);
  const [pastedText, setPastedText] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [selectedBulkEvents, setSelectedBulkEvents] = useState([]);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const fileInputRef = useRef(null);

  const emailRegexObj = useMemo(() => {
      if (!emailComplexityRegex) return null;
      try { return new RegExp(emailComplexityRegex); } catch { return null; }
  }, [emailComplexityRegex]);

  const validateEmail = (emailValue) => {
      if (emailRegexObj) {
          return emailRegexObj.test(emailValue);
      }
      const strictFallback = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
      return strictFallback.test(emailValue);
  };

  useEffect(() => {
    const fetchParameter = async (paramName) => {
        try {
            const cleanUrl = VIPSDataServiceURL.replace(/\/$/, "");
            const response = await axios.post(
                `${cleanUrl}/Misc/GetSystemParameterValueByName`, 
                {}, 
                { params: { name: paramName }, headers: { ...requestOptions.headers, 'Content-Type': 'application/json' } }
            );
            if (response.data) {
                return String(response.data).replace(/^"|"$/g, '').replace(/(\r\n|\n|\r)/gm, "").trim();
            }
            return null;
        } catch (err) {
            return null;
        }
    };

    const loadConfig = async () => {
        const emailRegex = await fetchParameter('EmailComplexity');
        if (emailRegex) setEmailComplexityRegex(decodeHtmlEntities(emailRegex));
    };

    loadConfig();
  }, []);

  useEffect(() => {
    if (loggedInUserId) fetchStoreGroups();
  }, [loggedInUserId]);

  const fetchStoreGroups = async () => {
    try {
        const cleanUrl = VIPSDataServiceURL.replace(/\/$/, "");
        const params = new URLSearchParams();
        params.append('userId', loggedInUserId);
        const response = await axios.post(`${cleanUrl}/User/GetUserStores`, null, { params: params, headers: { ...requestOptions.headers, 'Content-Type': 'application/json' } });
        const data = (typeof response.data === 'string') ? JSON.parse(response.data) : response.data;
        if (data) setStoreOptions(data);
    } catch (error) {
        setSnackbar({ open: true, message: 'Failed to load Store Groups. Please try again.', severity: 'error' });
    }
  };

  useEffect(() => {
    if (storeGroup) {
        fetchVendors(storeGroup);
        fetchNotificationTypes(storeGroup); 
        setVendorList(''); 
    } else {
        setVendorOptions([]);
        setVendorList('');
        setRows([]); 
    }
  }, [storeGroup]);

  useEffect(() => {
    if (storeGroup && vendorList) {
        fetchVendorAddresses(storeGroup, vendorList); 
    } else if (storeGroup && !vendorList && rows.length > 0) {
        setRows(prev => prev.map(r => ({ ...r, active: false, emails: [], originalEmails: [], isExisting: false })));
    }
  }, [vendorList, storeGroup]);

  const fetchNotificationTypes = async (selectedStore) => {
    setLoading(true);
    try {
        const cleanUrl = VIPSDataServiceURL.replace(/\/$/, "");
        const apiUrl = `${cleanUrl}/AccountAdministration/GetStoreNotificationTypes?storeGroup=${selectedStore}`;
        const response = await axios.post(apiUrl, {}, { headers: { ...requestOptions.headers, 'Content-Type': 'application/json' } });
        const data = (typeof response.data === 'string') ? JSON.parse(response.data) : response.data;
        if (data && Array.isArray(data)) {
            const mappedRows = data.map(item => ({ 
                id: item.value, label: item.name, active: false, emails: [], originalEmails: [], isExisting: false 
            }));
            setRows(mappedRows);
        } else {
            setRows([]);
        }
    } catch (error) {
        setRows([]);
    } finally {
        setLoading(false);
    }
  };

  const fetchVendorAddresses = async (store, vendorId) => {
    setLoading(true);
    try {
        const cleanUrl = VIPSDataServiceURL.replace(/\/$/, "");
        const apiUrl = `${cleanUrl}/Customer/GetStoreVendorNotificationAddresses?storeGroup=${store}&vendorId=${vendorId}`;
        const response = await axios.post(apiUrl, {}, { headers: { ...requestOptions.headers, 'Content-Type': 'application/json' } });
        const data = (typeof response.data === 'string') ? JSON.parse(response.data) : response.data;
        if (data && Array.isArray(data)) {
            setRows(prevRows => prevRows.map(row => {
                const match = data.find(d => String(d.notifType) === String(row.id));
                if (match) {
                    const dbEmails = match.emailAddresses ? match.emailAddresses.split(',').map(e => e.trim()).filter(e => e) : [];
                    return { ...row, active: match.isEnabled, emails: dbEmails, originalEmails: dbEmails, isExisting: true };
                }
                return { ...row, active: false, emails: [], originalEmails: [], isExisting: false }; 
            }));
        }
    } catch (error) {
        setSnackbar({ open: true, message: 'Failed to load vendor emails', severity: 'error' });
    } finally {
        setLoading(false);
    }
  };

  const handleProcessRow = async (updatedRow) => {
    if (!storeGroup || !vendorList) {
        setSnackbar({ open: true, message: 'Please select a Store and Vendor first.', severity: 'warning' });
        return false;
    }

    const callApi = async (reqType) => {
        const payload = {
            storeGroup: storeGroup, vendorId: vendorList, notifType: updatedRow.id,
            isEnabled: updatedRow.active, emailAddresses: updatedRow.emails.join(','),
            oldEmailAddresses: updatedRow.originalEmails ? updatedRow.originalEmails.join(',') : "",
            requestType: reqType, updatedBy: loggedInUserId
        };
        const cleanUrl = VIPSDataServiceURL.replace(/\/$/, "");
        const apiUrl = `${cleanUrl}/AccountAdministration/UpdateVendorNotificationAddresses`;
        return await axios.post(apiUrl, payload, { headers: { ...requestOptions.headers, 'Content-Type': 'application/json' } });
    };

    try {
        let initialReqType = updatedRow.isExisting ? 3 : 2;
        let response = await callApi(initialReqType);
        
        let result = response.data;
        if (typeof result === 'string') { try { result = JSON.parse(result); } catch (e) {} }

        if (initialReqType === 2 && result && result.success === false && result.message && result.message.includes("Mapping already exists")) {
            response = await callApi(3); 
            result = response.data;
            if (typeof result === 'string') { try { result = JSON.parse(result); } catch (e) {} }
        } else if (initialReqType === 3 && result && result.success === false) {
             response = await callApi(2); 
             result = response.data;
             if (typeof result === 'string') { try { result = JSON.parse(result); } catch (e) {} }
        }

        if (result && (result === true || result.success === true || result.Success === true || result.success === "true")) {
             const newRows = rows.map(r => r.id === updatedRow.id ? { ...updatedRow, isExisting: true, originalEmails: updatedRow.emails } : r );
             setRows(newRows);
             return true; 
        } else {
             const msg = result?.message || result?.Message || 'Save failed.';
             setSnackbar({ open: true, message: msg, severity: 'error' });
             return false;
        }
    } catch (error) {
        setSnackbar({ open: true, message: 'Error saving changes', severity: 'error' });
        return false;
    }
  };

  const fetchVendors = async (selectedStore) => {
    try {
        const cleanUrl = VIPSDataServiceURL.replace(/\/$/, "");
        const apiUrl = `${cleanUrl}/AccountAdministration/GetStoreVendors?storeGrp=${selectedStore}&userId=${loggedInUserId}`;
        const response = await axios.post(apiUrl, {}, { headers: { ...requestOptions.headers, 'Content-Type': 'application/json' } });
        const data = (typeof response.data === 'string') ? JSON.parse(response.data) : response.data;
        if (data && Array.isArray(data)) setVendorOptions(data);
        else setVendorOptions([]);
    } catch (error) {
        setVendorOptions([]);
        // Added user notification
        setSnackbar({ open: true, message: 'Failed to load Vendors. Please try again.', severity: 'error' });
    }
  };
  const handleToggle = async (id) => {
    const rowToUpdate = rows.find(r => r.id === id);
    if(!rowToUpdate) return;
    const updatedRow = { ...rowToUpdate, active: !rowToUpdate.active };
    setRows(rows.map(row => row.id === id ? updatedRow : row));
    await handleProcessRow(updatedRow);
  };

  const handleAddEmail = async (id, event) => {
    if (event.key === 'Enter' && currentInput[id]) {
      event.preventDefault();
      const newEmail = currentInput[id].trim();
      if (newEmail && validateEmail(newEmail)) {
        const rowToUpdate = rows.find(r => r.id === id);
        if (rowToUpdate && !rowToUpdate.emails.includes(newEmail)) {
            const updatedRow = { ...rowToUpdate, emails: [...rowToUpdate.emails, newEmail] };
            setRows(rows.map(row => row.id === id ? updatedRow : row));
            setCurrentInput({ ...currentInput, [id]: '' });
            await handleProcessRow(updatedRow);
        }
      } else if (newEmail) {
          setSnackbar({ open: true, message: 'Invalid email format based on system rules', severity: 'error' });
      }
    }
  };

  const removeEmail = async (id, emailToRemove) => {
    const rowToUpdate = rows.find(r => r.id === id);
    if(rowToUpdate) {
        const updatedRow = { ...rowToUpdate, emails: rowToUpdate.emails.filter(e => e !== emailToRemove) };
        setRows(rows.map(row => row.id === id ? updatedRow : row));
        await handleProcessRow(updatedRow);
    }
  };

  const clearAllEmails = async (id) => {
    const rowToUpdate = rows.find(r => r.id === id);
    if(rowToUpdate) {
        const updatedRow = { ...rowToUpdate, emails: [] };
        setRows(rows.map(row => row.id === id ? updatedRow : row));
        await handleProcessRow(updatedRow);
    }
  };

  const handleOpenUpload = () => {
      if (!storeGroup) {
          setSnackbar({ open: true, message: 'Please select a Store Group first to load Event Types.', severity: 'warning' });
          return;
      }
      setOpenUpload(true);
  };

  const handleCloseUpload = () => {
    setOpenUpload(false);
    setPastedText('');
    setUploadedFile(null);
    setSelectedBulkEvents([]);
    setBulkLoading(false);
    setIsSelectOpen(false);
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setUploadedFile(e.target.files[0]);
    }
  };

  const processEmails = (rawText) => {
    const items = rawText.split(/[\n,;\s]+/);
    const validEmails = items.map(item => item.trim()).filter(item => validateEmail(item));
    return [...new Set(validEmails)];
  };

  const handleBulkSubmit = async () => {
    setBulkLoading(true);

    if (uploadTab === 0) {
      if (!vendorList) {
          setSnackbar({ open: true, message: 'Please select a Vendor List before pasting emails.', severity: 'warning' });
          setBulkLoading(false);
          return;
      }

      const newEmails = processEmails(pastedText);
      if (newEmails.length > 0) {
        const updatedRowsPromises = rows.map(async (row) => {
          if (!selectedBulkEvents.includes(row.id)) return row;

          const combined = [...row.emails, ...newEmails];
          const unique = [...new Set(combined)];
          const updatedRow = { ...row, emails: unique };
          const success = await handleProcessRow(updatedRow);
          return success ? updatedRow : row;
        });

        const finalRows = await Promise.all(updatedRowsPromises);
        setRows(finalRows);
        setSnackbar({ open: true, message: 'Bulk pasted emails added successfully', severity: 'success' });
        handleCloseUpload();
      } else {
        alert("No valid emails found to add.");
      }
      setBulkLoading(false);

    } else if (uploadTab === 1 && uploadedFile) {
      try {
        const data = await uploadedFile.arrayBuffer();
        const workbook = XLSX.read(data, { type: 'array' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
           alert("The uploaded Excel file is empty.");
           setBulkLoading(false);
           return;
        }

        const eventNameToIdMap = {};
        rows.forEach(r => {
            eventNameToIdMap[r.label.toLowerCase().trim()] = r.id;
        });

        let successCount = 0;
        let failCount = 0;
        const cleanUrl = VIPSDataServiceURL.replace(/\/$/, "");

        for (const excelRow of jsonData) {
            const vendorKey = Object.keys(excelRow).find(key => key.toLowerCase().trim() === 'vendor code');
            const rowVendorCode = vendorKey ? String(excelRow[vendorKey]).trim() : '';

            if (!rowVendorCode) continue;

            for (const [columnName, cellValue] of Object.entries(excelRow)) {
                if (columnName === vendorKey) continue;

                const normalizedColumnName = columnName.toLowerCase().trim();
                const notifTypeId = eventNameToIdMap[normalizedColumnName];

                if (!notifTypeId) continue;

                const rawEmails = String(cellValue || '');
                const processedEmails = processEmails(rawEmails);

                if (processedEmails.length === 0) continue;

                let existingDbEmails = [];
                let isExistingInDb = false;

                try {
                    const existingDataRes = await axios.post(
                        `${cleanUrl}/Customer/GetStoreVendorNotificationAddresses?storeGroup=${storeGroup}&vendorId=${rowVendorCode}`, 
                        {}, { headers: { ...requestOptions.headers, 'Content-Type': 'application/json' } }
                    );
                    
                    const existingData = typeof existingDataRes.data === 'string' ? JSON.parse(existingDataRes.data) : existingDataRes.data;
                    if (existingData && Array.isArray(existingData)) {
                        const match = existingData.find(d => String(d.notifType) === String(notifTypeId));
                        if (match) {
                            isExistingInDb = true;
                            existingDbEmails = match.emailAddresses ? match.emailAddresses.split(',').map(e => e.trim()).filter(e => e) : [];
                        }
                    }
                } catch (err) {
                    failCount++;
                    continue; 
                }

                const combinedEmails = [...new Set([...existingDbEmails, ...processedEmails])];

                const payload = {
                    storeGroup: storeGroup,
                    vendorId: rowVendorCode,
                    notifType: notifTypeId, 
                    isEnabled: true,
                    emailAddresses: combinedEmails.join(','),
                    oldEmailAddresses: existingDbEmails.join(','),
                    requestType: isExistingInDb ? 3 : 2, 
                    updatedBy: loggedInUserId
                };

                try {
                    const updateUrl = `${cleanUrl}/AccountAdministration/UpdateVendorNotificationAddresses`;
                    let response = await axios.post(updateUrl, payload, { headers: { ...requestOptions.headers, 'Content-Type': 'application/json' } });
                    let result = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;

                    if (payload.requestType === 2 && result && result.success === false && result.message && result.message.includes("Mapping already exists")) {
                        payload.requestType = 3;
                        await axios.post(updateUrl, payload, { headers: { ...requestOptions.headers, 'Content-Type': 'application/json' } });
                    } else if (payload.requestType === 3 && result && result.success === false) {
                        payload.requestType = 2;
                        await axios.post(updateUrl, payload, { headers: { ...requestOptions.headers, 'Content-Type': 'application/json' } });
                    }
                    successCount++;
                } catch (err) {
                    failCount++;
                }
            }
        }

        setSnackbar({ 
            open: true, 
            message: `Excel upload complete! ${successCount} events updated, ${failCount} failed.`, 
            severity: failCount > 0 ? 'warning' : 'success' 
        });

        if (storeGroup && vendorList) {
            fetchVendorAddresses(storeGroup, vendorList);
        }
        
        handleCloseUpload();

      } catch (error) {
        setSnackbar({ open: true, message: 'Failed to process Excel file', severity: 'error' });
      } finally {
        setBulkLoading(false);
      }
    }
  };

  const isSubmitDisabled = () => {
      if (bulkLoading) return true;
      if (uploadTab === 0) return !pastedText || selectedBulkEvents.length === 0;
      if (uploadTab === 1) return !uploadedFile;
      return true;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: BACKGROUND_BG }}>
      <Box component="main" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', pt: 6, pb: 8 }}>
        <Container maxWidth="xl">
          
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ width: 6, height: 32, backgroundColor: PRIMARY_COLOR, borderRadius: 4 }} />
              <Typography variant="h5" sx={{ color: '#1e293b', fontWeight: 700, letterSpacing: '0.5px' }}>
                Email Notification Admin
              </Typography>
          </Box>

          <Paper elevation={0} sx={{ p: 3, backgroundColor: 'white', borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.03)', mb: 2, border: '1px solid #f0f0f0' }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: '700', color: '#64748b', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '1px' }}>Filter Records</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <CustomSelect select fullWidth label="Store Group" value={storeGroup} onChange={(e) => setStoreGroup(e.target.value)} InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }} size="small">
                  <MenuItem value="" disabled>--Select--</MenuItem>
                  {storeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </CustomSelect>
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomSelect select fullWidth label="Vendor List" value={vendorList} onChange={(e) => setVendorList(e.target.value)} InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }} disabled={!storeGroup} size="small">
                  <MenuItem value="" disabled>--Select--</MenuItem>
                  {vendorOptions.map((vendor, index) => {
                      const vId = vendor.vendorId ? vendor.vendorId.trim() : "";
                      const vName = vendor.vendorName || "";
                      const label = vId ? `${vId} - ${vName}` : vName;
                      const value = vId || `unknown-${index}`; 
                      return <MenuItem key={index} value={value}>{label}</MenuItem>;
                  })}
                </CustomSelect>
              </Grid>
            </Grid>
          </Paper>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', mb: 0, px: 0 }}>
            <Button 
              variant="contained" 
              onClick={handleOpenUpload} 
              startIcon={<CloudUploadIcon />} 
              sx={{ height: 40, borderRadius: '12px 12px 0 0', textTransform: 'none', fontWeight: 600, px: 4, background: BUTTON_GRADIENT }}
            >
              Bulk Upload
            </Button>
          </Box>

          <Card sx={{ borderRadius: '16px 0 0 0', boxShadow: '0px 10px 30px rgba(0,0,0,0.05)', border: 'none', overflow: 'hidden' }}>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow> 
                    <TableCell align="center" sx={headerStyle} width="30%">Notification Event Type</TableCell>
                    <TableCell align="center" sx={headerStyle} width="10%">Enable</TableCell>
                    <TableCell align="left" sx={headerStyle}>Addresses</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                      <TableRow><TableCell colSpan={3} align="center" sx={{ py: 10 }}><CircularProgress sx={{ color: PRIMARY_COLOR }} /><Typography variant="body2" sx={{ mt: 2, color: '#555' }}>Loading records...</Typography></TableCell></TableRow>
                  ) : rows.length === 0 ? (
                      <TableRow><TableCell colSpan={3} align="center" sx={{ py: 6, color: '#757575', fontStyle: 'italic' }}>Select a Store Group and Vendor to view settings</TableCell></TableRow>
                  ) : rows.map((row, index) => (
                    <TableRow key={row.id} sx={{ '&:hover': { backgroundColor: HOVER_ROW_COLOR }, backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa' }}>
                      <TableCell align="center" sx={{ fontWeight: 600, color: '#334155', verticalAlign: 'middle' }}>{row.label}</TableCell>
                      <TableCell align="center" sx={{ verticalAlign: 'middle' }}>
                        <SlimSwitch checked={row.active} onChange={() => handleToggle(row.id)} />
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <PremiumChipContainer active={row.active}>
                              {row.emails.map((email) => (
                                <Chip 
                                  key={email} icon={<EmailIcon />} deleteIcon={<CancelIcon />} label={email} size="small" 
                                  onDelete={() => removeEmail(row.id, email)} 
                                  sx={{ 
                                      height: '30px', bgcolor: '#F0F8FF', border: '1px solid #BFDBFE', color: '#1565C0', fontWeight: 700, borderRadius: '6px', 
                                      '& .MuiChip-icon': { color: '#546E7A !important', fontSize: '18px' },
                                      '& .MuiChip-deleteIcon': { color: '#ef5350 !important', fontSize: '18px', '&:hover': { color: '#d32f2f !important' } } 
                                  }} 
                                />
                              ))}
                              <TextField
                                variant="standard" placeholder={row.active ? "Type email and press Enter..." : ""}
                                value={currentInput[row.id] || ''}
                                onKeyDown={(e) => handleAddEmail(row.id, e)}
                                onChange={(e) => setCurrentInput({ ...currentInput, [row.id]: e.target.value })}
                                InputProps={{ disableUnderline: true, sx: { fontSize: '13.5px', ml: 1, flexGrow: 1, minWidth: '200px', color: '#1e293b', '&::placeholder': { color: '#94a3b8', opacity: 1 } } }}
                              />
                            </PremiumChipContainer>
                            {row.emails.length > 0 && row.active && (
                              <Tooltip title="Clear All">
                                <IconButton size="small" onClick={() => clearAllEmails(row.id)} sx={{ ml: 1.5, color: '#ef4444', bgcolor: alpha('#ef4444', 0.08), borderRadius: '8px', '&:hover': { bgcolor: alpha('#ef4444', 0.15) } }}>
                                  <DeleteIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
          
          <Dialog open={openUpload} onClose={handleCloseUpload} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
            <DialogTitle sx={dialogHeaderStyle}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CloudUploadIcon fontSize="medium" sx={{ color: '#ffffff !important' }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#ffffff !important', letterSpacing: '0.5px' }}>Bulk Upload Emails</Typography>
              </Box>
              <IconButton onClick={handleCloseUpload} disabled={bulkLoading} sx={{ color: '#ffffff !important' }}><CloseIcon /></IconButton>
            </DialogTitle>
            
            <DialogContent sx={{ backgroundColor: '#fafafa', py: 4 }} dividers>
              
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={uploadTab} onChange={(e, v) => setUploadTab(v)} aria-label="upload tabs" disabled={bulkLoading}>
                  <Tab icon={<ContentPasteIcon fontSize="small" />} iconPosition="start" label="Paste Emails" />
                  <Tab icon={<FileIcon fontSize="small" />} iconPosition="start" label="Upload File" />
                </Tabs>
              </Box>

              {uploadTab === 0 ? (
                <Box>
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel id="bulk-event-select-label">Apply to Event Types</InputLabel>
                    <Select
                      labelId="bulk-event-select-label" 
                      multiple 
                      value={selectedBulkEvents}
                      open={isSelectOpen}
                      onOpen={() => setIsSelectOpen(true)}
                      onClose={() => setIsSelectOpen(false)}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (Array.isArray(value) && !value.includes('CLOSE_ACTION')) {
                          setSelectedBulkEvents(value);
                        }
                      }}
                      input={<OutlinedInput label="Apply to Event Types" sx={{ borderRadius: 2, backgroundColor: '#fff' }} />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => {
                            const eventLabel = rows.find(r => r.id === value)?.label || value;
                            return <Chip key={value} label={eventLabel} size="small" color="primary" variant="outlined" />;
                          })}
                        </Box>
                      )}
                      MenuProps={{ PaperProps: { sx: { maxHeight: 350 } } }} 
                    >
                      {rows.map((row) => (
                        <MenuItem key={row.id} value={row.id}>
                          <Checkbox checked={selectedBulkEvents.indexOf(row.id) > -1} />
                          <ListItemText primary={row.label} />
                        </MenuItem>
                      ))}
                      
                      <MenuItem 
                        value="CLOSE_ACTION"
                        disableRipple
                        onClick={() => setIsSelectOpen(false)}
                        sx={{ 
                          position: 'sticky', bottom: 0, backgroundColor: '#ffffff', zIndex: 2, 
                          borderTop: '1px solid #e0e0e0', mt: 1, pt: 1.5, pb: 1.5,
                          '&:hover': { backgroundColor: '#ffffff' } 
                        }}
                      >
                        <Button fullWidth variant="contained" sx={{ background: BUTTON_GRADIENT, fontWeight: 'bold' }}>
                          OK
                        </Button>
                      </MenuItem>
                    </Select>
                  </FormControl>
                  <TextField multiline rows={6} fullWidth placeholder="Paste emails here separated by commas, spaces, or newlines..." value={pastedText} onChange={(e) => setPastedText(e.target.value)} sx={premiumInputStyle} disabled={bulkLoading} />
                </Box>
              ) : (
                <Box>
                  <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                    <strong>Note:</strong> Your Excel file must have a <code>Vendor Code</code> column. Other columns should exactly match your Notification Event Names (e.g. <code>Print order Received</code>).
                  </Typography>
                  <UploadBox onClick={() => !bulkLoading && fileInputRef.current.click()} sx={{ opacity: bulkLoading ? 0.5 : 1 }}>
                    <input type="file" accept=".xlsx" hidden ref={fileInputRef} onChange={handleFileChange} disabled={bulkLoading} />
                    <CloudUploadIcon sx={{ fontSize: 48, color: '#1976d2', mb: 1 }} />
                    <Typography variant="body1" fontWeight={600} color="#1b2559">{uploadedFile ? uploadedFile.name : "Click to Upload Excel File"}</Typography>
                    <Typography variant="caption" color="text.secondary">Supports .xlsx only</Typography>
                  </UploadBox>
                </Box>
              )}
            </DialogContent>
            
            <DialogActions sx={{ backgroundColor: '#f4f2e9', p: 3, borderTop: '1px solid #e0e0e0', justifyContent: 'flex-end' }}>
              <Button onClick={handleCloseUpload} disabled={bulkLoading} sx={{ color: '#64748b', fontWeight: 600, mr: 1, textTransform: 'none' }}>Cancel</Button>
              <Button 
                variant="contained" 
                onClick={handleBulkSubmit}
                disabled={isSubmitDisabled()}
                startIcon={bulkLoading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                sx={{ 
                  background: BUTTON_GRADIENT, px: 3, borderRadius: 1, textTransform: 'none', fontWeight: 600, color: '#ffffff',
                  '&:hover': { background: 'linear-gradient(135deg, #0d47a1 0%, #002171 100%)' },
                  '&.Mui-disabled': { background: '#e0e0e0', color: '#9e9e9e', boxShadow: 'none' }
                }}
              >
                {bulkLoading ? 'Processing...' : 'Add Emails'}
              </Button>
            </DialogActions>
          </Dialog>

          <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
              <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>{snackbar.message}</Alert>
          </Snackbar>

        </Container>
      </Box>
      <Newfooter/>
    </Box>
  );
};

export default EmailAdmin;